import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import contractApi from '../../../APIs/contract';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import AdditionalRequirement from '../../../components/Contract/AdditionalRequirement';
import ClientInformation from '../../../components/Contract/ClientInformation';
import ClientRequirements from '../../../components/Contract/ClientRequirements';
import ContractDetail from '../../../components/Contract/ContractDetail';
import CostShipping from '../../../components/Contract/CostShipping';
import JobInformation from '../../../components/Contract/JobInformation';
import PropertyInformation from '../../../components/Contract/PropertyInformation';
import escConstants from '../../../constants';
import {
  Button,
  Divider,
  NavigateBack,
  Tag,
  Typography,
} from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import { humanReadableDate, requestAPI } from '../../../utils';
import { ViewJobContainerStyled } from '../../BrowseJobs/BrowseJobDetails/style';
import { ActionsContainer } from './style';

type Props = {};

const ContractDetails = (_props: Props) => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contractActionStatus, setContractActionStatus] = useState<{
    openModal: boolean;
    loading: boolean;
    message: string;
    status?: 'ACCEPT' | 'REJECT';
  }>({
    openModal: false,
    loading: false,
    message: '',
    status: undefined,
  });
  const contractDetails = useSelector(
    (state: RootState) => state.contractDetails
  );

  const contractStatus = contractDetails?.contractStatus;

  const fetchContractDetails = async (id: string) => {
    try {
      requestAPI(contractApi.getContractDetails(id));
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (contractId) {
      fetchContractDetails(contractId);
    }
  }, []);

  const onCloseModal = () => {
    setContractActionStatus({
      loading: false,
      message: '',
      openModal: false,
      status: undefined,
    });
  };

  const onContractAction = (status: 'ACCEPT' | 'REJECT') => {
    setContractActionStatus((prev) => ({
      ...prev,
      openModal: true,
      message: `Are you sure you want to ${status} this contract?`,
      status,
    }));
  };

  const handleContractActions = async (status: 'ACCEPT' | 'REJECT') => {
    try {
      setContractActionStatus((prev) => ({
        ...prev,
        loading: true,
      }));
      if (contractId) {
        await requestAPI(
          contractApi.updateContractStatus(contractId, {
            status,
          })
        );
      }
      if (contractId) {
        fetchContractDetails(contractId);
      }
      onCloseModal();
      if (status === 'ACCEPT') {
        toast(escConstants.toastMessages.contractAccepted);
      } else {
        toast(escConstants.toastMessages.contractRejected);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleViewJob = () => {
    if (contractDetails?.contractStatus === 'ACCEPT') {
      navigate(
        `/jobs/details/${contractDetails?.jobAndProposal}/jobs/${contractDetails?.jobId?._id}`
      );
    } else {
      navigate(`/browse-jobs/${contractDetails?.jobId?._id}`);
    }
  };

  const buildingInfo =
    typeof contractDetails?.jobId?.building === 'string'
      ? undefined
      : contractDetails?.jobId?.building;

  return (
    <ViewJobContainerStyled>
      <div
        style={{
          position: 'sticky',
          top: '60px',
          zIndex: '999',
          paddingTop: '5px',
          background: '#fff',
        }}
      >
        <NavigateBack />
      </div>
      <div className="contents">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="normal" style={{ marginBottom: '15px' }}>
            Contract Details{' '}
          </Typography>
          <div>
            {contractStatus === 'ACCEPT' && <Tag type="success">ACCEPTED</Tag>}
            {contractStatus === 'REJECT' && <Tag type="danger">REJECTED</Tag>}
            {contractStatus === 'CANCEL' && <Tag type="danger">CANCELLED</Tag>}
            {contractStatus === 'PENDING' && <Tag type="warning">PENDING</Tag>}
            <Button onClick={handleViewJob} style={{ marginLeft: '10px' }}>
              View Job
            </Button>
          </div>
        </div>
        <Divider />

        <JobInformation
          createdAt={humanReadableDate(contractDetails?.jobId?.createdAt || '')}
          jobTitle={contractDetails?.jobId?.jobTitle || ''}
          notes={contractDetails?.jobId?.notes || ''}
        />
        <Divider />
        <PropertyInformation
          buildingName={buildingInfo?.buildingName || ''}
          buildingAddress={buildingInfo?.buildingAddress || ''}
        />
        <Divider />
        <CostShipping
          pricing={contractDetails?.jobId?.pricing || ''}
          shippingAddress={contractDetails?.jobId?.shippingAddress || ''}
        />
        <Divider />
        <ClientInformation
          fullName={contractDetails?.createdBy?.fullName || ''}
          email={contractDetails?.createdBy?.emailAddress || ''}
        />
        <Divider />
        <ClientRequirements
          drawingSize={contractDetails?.jobId?.drawingSize || ''}
          frameColor={contractDetails?.jobId?.frameColor || ''}
          logoResolution={contractDetails?.jobId?.logoResolution || ''}
          drawingMaterialType={
            contractDetails?.jobId?.drawingMaterialType || ''
          }
          roundedEdge={
            contractDetails?.jobId?.roundedEdge === true ? 'Yes' : 'No'
          }
          logo={contractDetails?.jobId?.logo || ['']}
        />
        <Divider />
        <AdditionalRequirement title={'Test Title'} value={'Test Value'} />
        <Divider />
        <ContractDetail />
        <Divider spaceY="20px" />

        {contractStatus === 'PENDING' ? (
          <>
            <Typography
              variant="normal"
              strong
              fontColor="secondary"
              className="section-title"
            >
              ACTIONS
            </Typography>
            <ActionsContainer>
              <Button
                variant="secondary"
                size="sm"
                width="122px"
                onClick={() => onContractAction('ACCEPT')}
                isLoading={loading}
              >
                Accept Contract
              </Button>
              <Button
                variant="danger"
                size="sm"
                width="122px"
                onClick={() => onContractAction('REJECT')}
              >
                Reject Contract
              </Button>
            </ActionsContainer>
          </>
        ) : (
          ''
        )}
      </div>
      <ConfirmationModal
        message={contractActionStatus.message}
        isOpen={contractActionStatus?.openModal}
        onClose={onCloseModal}
        title="Contract Action"
        okText={capitalize(contractActionStatus.status)}
        onOk={() => {
          if (contractActionStatus.status) {
            handleContractActions(contractActionStatus?.status);
          }
        }}
      />
    </ViewJobContainerStyled>
  );
};

export default ContractDetails;
